import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { MdAdd, MdDelete, MdContentCopy, MdAttachMoney, MdWarning, MdEdit } from 'react-icons/md';
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { themes } from '../../theme.ts';
import Toast from '../../components/common/Toast.tsx';
import DeleteConfirmation from '../../components/common/DeleteConfirmation.tsx';

interface Coupon {
    id: string;
    created_at: string;
    updated_at: string;
    is_deleted: boolean;
    others: any;
    code: string;
    discount_type: 'FLAT' | 'PERCENTAGE';
    discount_value: string;
    max_discount: string;
    min_order_value: string;
    valid_from: string;
    valid_to: string;
    usage_limit: number;
    used_count: number;
    active: boolean;
}

const AdminCoupons = () => {
    const { themeName } = useTheme();
    const [coupons, setCoupons] = useState<Coupon[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' | 'info' } | null>(null);

    // Edit Mode State
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingCouponId, setEditingCouponId] = useState<string | null>(null);

    // Delete Confirmation State
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [couponToDelete, setCouponToDelete] = useState<string | null>(null);

    const initialFormState = {
        code: '',
        discount_type: 'FLAT',
        discount_value: '',
        min_order_value: '',
        max_discount: '',
        usage_limit: 2147483647,
        active: true,
        valid_from: '',
        valid_to: ''
    };

    const [formData, setFormData] = useState(initialFormState);

    const rawData = localStorage.getItem('user');
    const cred = rawData ? JSON.parse(rawData) : null;
    const token = cred?.access;

    const fetchCoupons = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_DB_URI}/users/coupons/get-all/`, {
                headers: { "Authorization": `Bearer ${token}` }
            });
            setCoupons(response.data);
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCoupons();
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const openCreateModal = () => {
        setIsEditMode(false);
        setEditingCouponId(null);
        setFormData(initialFormState);
        setIsModalOpen(true);
    };

    const openEditModal = (coupon: Coupon) => {
        setIsEditMode(true);
        setEditingCouponId(coupon.id);

        // Populate form with coupon data
        // Note: Dates might need formatting to match input type="datetime-local" (YYYY-MM-DDThh:mm)
        // Adjusting date format if necessary
        const formatForInput = (dateStr: string) => dateStr ? new Date(dateStr).toISOString().slice(0, 16) : '';

        setFormData({
            code: coupon.code,
            discount_type: coupon.discount_type as any, // Cast if necessary or ensure types match
            discount_value: coupon.discount_value,
            min_order_value: coupon.min_order_value,
            max_discount: coupon.max_discount,
            usage_limit: coupon.usage_limit,
            active: coupon.active,
            valid_from: formatForInput(coupon.valid_from),
            valid_to: formatForInput(coupon.valid_to)
        });
        setIsModalOpen(true);
    };

    const handleSubmit = async () => {
        setIsLoading(true);

        const payload = {
            others: {},
            code: formData.code,
            discount_type: formData.discount_type,
            discount_value: String(formData.discount_value),
            max_discount: String(formData.max_discount || formData.discount_value),
            min_order_value: String(formData.min_order_value),
            valid_from: formData.valid_from ? new Date(formData.valid_from).toISOString() : null,
            valid_to: formData.valid_to ? new Date(formData.valid_to).toISOString() : null,
            usage_limit: Number(formData.usage_limit),
            active: formData.active
        };

        try {
            if (isEditMode && editingCouponId) {
                // PATCH Request for Update
                await axios.patch(`${process.env.REACT_APP_DB_URI}/users/coupons/${editingCouponId}/update/`, payload, {
                    headers: { "Authorization": `Bearer ${token}` }
                });
                setToast({ message: "Coupon updated successfully", type: "success" });
            } else {
                // POST Request for Create
                await axios.post(`${process.env.REACT_APP_DB_URI}/users/coupons/create/`, payload, {
                    headers: { "Authorization": `Bearer ${token}` }
                });
                setToast({ message: "Coupon created successfully", type: "success" });
            }

            setIsModalOpen(false);
            fetchCoupons();
        } catch (err: any) {
            console.error(err);
            setToast({ message: err.response?.data?.message || err.response?.data?.valid_from?.[0] || err.response?.data?.valid_to?.[0] || "Failed to save coupon", type: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    const confirmDelete = (id: string) => {
        setCouponToDelete(id);
        setIsDeleteModalOpen(true);
    };

    const executeDelete = async () => {
        if (!couponToDelete) return;
        try {
            await axios.delete(`${process.env.REACT_APP_DB_URI}/users/coupons/delete/${couponToDelete}/`, {
                headers: { "Authorization": `Bearer ${token}` }
            });
            setToast({ message: "Coupon deleted", type: "success" });
            fetchCoupons();
        } catch (err: any) {
            setToast({ message: "Failed to delete coupon", type: "error" });
        } finally {
            setIsDeleteModalOpen(false);
            setCouponToDelete(null);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        setToast({ message: "Copied to clipboard!", type: "success" });
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="p-6 min-h-screen" style={{ backgroundColor: themes[themeName].colors.background }}>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold" style={{ color: themes[themeName].colors.text }}>Manage Coupons</h1>
                <button
                    onClick={openCreateModal}
                    className="flex items-center px-4 py-2 rounded-lg bg-teal-500 text-white hover:opacity-90 transition"
                    style={{ backgroundColor: themes[themeName].colors.primary }}
                >
                    <MdAdd className="mr-2" /> Create Coupon
                </button>
            </div>

            {isLoading && <p style={{ color: themes[themeName].colors.textSecondary }}>Loading...</p>}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
                {coupons.map(coupon => (
                    <div key={coupon.id} className="relative p-5 rounded-xl shadow-lg border transition-transform hover:scale-[1.01]" style={{ backgroundColor: themes[themeName].colors.surface, borderColor: themes[themeName].colors.border }}>

                        {/* Status Badge */}
                        <div className="absolute top-4 right-4">
                            <span className={`text-xs font-bold px-3 py-1 rounded-full ${coupon.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                {coupon.active ? 'ACTIVE' : 'INACTIVE'}
                            </span>
                        </div>

                        {/* Code Header */}
                        <div className="flex items-center space-x-2 mb-4">
                            <h3 className="text-xl font-extrabold text-gray-800 tracking-wide bg-gray-100 px-3 py-1 rounded-md border border-gray-200 cursor-pointer hover:bg-gray-200" onClick={() => copyToClipboard(coupon.code)}>
                                {coupon.code}
                            </h3>
                            <button onClick={() => copyToClipboard(coupon.code)} className="text-gray-400 hover:text-gray-600">
                                <MdContentCopy size={16} />
                            </button>
                        </div>

                        {/* Main Info Grid */}
                        <div className="grid grid-cols-2 gap-y-3 gap-x-4 mb-4 text-sm">
                            <div className="col-span-2">
                                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Discount</p>
                                <p className="text-lg font-bold" style={{ color: themes[themeName].colors.primary }}>
                                    {coupon.discount_type === 'PERCENTAGE' ? `${parseFloat(coupon.discount_value)}% OFF` : `Rs ${coupon.discount_value} OFF`}
                                </p>
                            </div>

                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Min Order</p>
                                <div className="flex items-center font-medium" style={{ color: themes[themeName].colors.text }}>
                                    <MdAttachMoney className="mr-1 text-gray-400" /> Rs {coupon.min_order_value}
                                </div>
                            </div>

                            {coupon.discount_type === 'PERCENTAGE' && (
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Max Discount</p>
                                    <p className="font-medium" style={{ color: themes[themeName].colors.text }}>Rs {coupon.max_discount}</p>
                                </div>
                            )}

                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Usage</p>
                                <p className="font-medium" style={{ color: themes[themeName].colors.text }}>
                                    {coupon.used_count} / {coupon.usage_limit}
                                </p>
                            </div>
                        </div>

                        {/* Validity Section */}
                        <div className="bg-gray-50 p-3 rounded-lg text-xs space-y-1 mb-4 border border-gray-100">
                            <div className="flex justify-between">
                                <span className="text-gray-500 font-medium">Valid From:</span>
                                <span className="text-gray-700">{formatDate(coupon.valid_from)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500 font-medium">Valid To:</span>
                                <span className="text-gray-700">{formatDate(coupon.valid_to)}</span>
                            </div>
                        </div>

                        {/* Footer Actions & Meta */}
                        <div className="flex justify-between items-center border-t pt-3 mt-2 border-dashed border-gray-200">
                            <span className="text-[10px] text-gray-400">Created: {new Date(coupon.created_at).toLocaleDateString()}</span>
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => openEditModal(coupon)}
                                    className="flex items-center text-blue-500 hover:text-blue-600 hover:bg-blue-50 px-2 py-1 rounded transition-colors text-sm font-medium"
                                >
                                    <MdEdit size={16} className="mr-1" /> Edit
                                </button>
                                <button
                                    onClick={() => confirmDelete(coupon.id)}
                                    className="flex items-center text-red-500 hover:text-red-600 hover:bg-red-50 px-2 py-1 rounded transition-colors text-sm font-medium"
                                >
                                    <MdDelete size={16} className="mr-1" /> Delete
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {coupons.length === 0 && !isLoading && (
                <div className="flex flex-col items-center justify-center mt-20 text-gray-400">
                    <MdWarning size={48} className="mb-2 opacity-50" />
                    <p className="text-lg">No coupons found.</p>
                </div>
            )}

            {isModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 px-4 backdrop-blur-sm">
                    <div className="w-full max-w-md p-6 rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto transform transition-all scale-100" style={{ backgroundColor: themes[themeName].colors.surface }}>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold" style={{ color: themes[themeName].colors.text }}>{isEditMode ? 'Edit Coupon' : 'Create New Coupon'}</h2>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
                        </div>

                        <div className="space-y-5">
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider mb-1 text-gray-500">Code</label>
                                <input name="code" value={formData.code} onChange={handleInputChange} className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all" placeholder="e.g. SAVE20" style={{ backgroundColor: themes[themeName].colors.background, color: themes[themeName].colors.text, borderColor: themes[themeName].colors.border }} />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider mb-1 text-gray-500">Type</label>
                                    <select name="discount_type" value={formData.discount_type} onChange={handleInputChange} className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" style={{ backgroundColor: themes[themeName].colors.background, color: themes[themeName].colors.text, borderColor: themes[themeName].colors.border }}>
                                        <option value="FLAT">Flat Amount</option>
                                        <option value="PERCENTAGE">Percentage</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider mb-1 text-gray-500">Value</label>
                                    <input type="number" name="discount_value" value={formData.discount_value} onChange={handleInputChange} className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" placeholder="50" style={{ backgroundColor: themes[themeName].colors.background, color: themes[themeName].colors.text, borderColor: themes[themeName].colors.border }} />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider mb-1 text-gray-500">Max Discount</label>
                                    <input type="number" name="max_discount" value={formData.max_discount} onChange={handleInputChange} className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" placeholder="100" style={{ backgroundColor: themes[themeName].colors.background, color: themes[themeName].colors.text, borderColor: themes[themeName].colors.border }} />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider mb-1 text-gray-500">Min Order</label>
                                    <input type="number" name="min_order_value" value={formData.min_order_value} onChange={handleInputChange} className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" placeholder="500" style={{ backgroundColor: themes[themeName].colors.background, color: themes[themeName].colors.text, borderColor: themes[themeName].colors.border }} />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider mb-1 text-gray-500">Valid From</label>
                                    <input type="datetime-local" name="valid_from" value={formData.valid_from} onChange={handleInputChange} className="w-full p-2 border rounded-lg text-sm" style={{ backgroundColor: themes[themeName].colors.background, color: themes[themeName].colors.text, borderColor: themes[themeName].colors.border }} />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider mb-1 text-gray-500">Valid To</label>
                                    <input type="datetime-local" name="valid_to" value={formData.valid_to} onChange={handleInputChange} className="w-full p-2 border rounded-lg text-sm" style={{ backgroundColor: themes[themeName].colors.background, color: themes[themeName].colors.text, borderColor: themes[themeName].colors.border }} />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider mb-1 text-gray-500">Usage Limit</label>
                                <input type="number" name="usage_limit" value={formData.usage_limit} onChange={handleInputChange} className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" style={{ backgroundColor: themes[themeName].colors.background, color: themes[themeName].colors.text, borderColor: themes[themeName].colors.border }} />
                            </div>

                            <div className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-100">
                                <input type="checkbox" name="active" checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} className="w-5 h-5 text-teal-600 rounded focus:ring-teal-500 mr-3" />
                                <label className="font-medium text-gray-700">Set as Active</label>
                            </div>
                        </div>

                        <div className="mt-8 flex justify-end space-x-3">
                            <button onClick={() => setIsModalOpen(false)} className="px-6 py-2.5 rounded-lg text-gray-600 hover:bg-gray-100 font-medium transition-colors">Cancel</button>
                            <button onClick={handleSubmit} className="px-6 py-2.5 rounded-lg bg-teal-500 text-white hover:bg-teal-600 font-medium shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5">{isEditMode ? 'Update' : 'Create'} Coupon</button>
                        </div>
                    </div>
                </div>
            )}

            <DeleteConfirmation
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={executeDelete}
                title="Delete Coupon"
                message="Are you sure you want to delete this coupon? Users will no longer be able to redeem it."
            />

            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} show={true} />}
        </div>
    );
};

export default AdminCoupons;
